import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  fetchComplianceRequirementsList,
  toggleComplianceRequirement,
  bulkToggleComplianceRequirements,
} from '@/_core/entities/report/api/report.endpoints';
import type { TComplianceRequirementListItem } from '@/_core/entities/report/models/types';
import { useToast } from '@/_core/shared/ui/AppToast/useToast';
import SearchInput from '@/_core/shared/ui/SearchInput/SearchInput.vue';

type TFilterKey = 'jurisdiction' | 'type' | 'sector' | 'enforcement';


export default /*#__PURE__*/_defineComponent({
  __name: 'ComplianceScopePage',
  setup(__props) {

const TYPES = ['Catalog', 'Framework', 'Guideline', 'Regulation', 'Standard'];
const SECTORS = ['Finance', 'General', 'Government', 'Healthcare'];
const ENFORCEMENTS = ['Mandatory', 'Voluntary'];
const JURISDICTIONS = ['Global', 'European Union', 'India', 'Egypt'];

const instance = getCurrentInstance();
const integrationId = computed(() => String(unref(instance?.proxy.$route)?.params?.id ?? ''));

const searchInput = ref('');
const search = ref('');
let searchDebounce: ReturnType<typeof setTimeout> | null = null;
watch(searchInput, (value) => {
  if (searchDebounce) clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    search.value = value;
  }, 300);
});
const filters = ref<{
  jurisdiction: string[];
  type: string[];
  sector: string[];
  enforcement: string[];
}>({ jurisdiction: [], type: [], sector: [], enforcement: [] });

const openDropdown = ref<string | null>(null);
const filtersRef = ref<HTMLElement | null>(null);

function onClickOutside(e: MouseEvent) {
  if (openDropdown.value && filtersRef.value && !filtersRef.value.contains(e.target as Node)) {
    openDropdown.value = null;
  }
}

onMounted(() => document.addEventListener('click', onClickOutside));
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside));

const queryClient = useQueryClient();
const toast = useToast();

const requirementsQuery = useQuery({
  queryKey: computed(() => [
    'complianceRequirementsList',
    integrationId.value,
    search.value,
    filters.value,
  ]),
  queryFn: () =>
    fetchComplianceRequirementsList({
      integrationId: integrationId.value,
      search: search.value,
      jurisdiction: filters.value.jurisdiction,
      type: filters.value.type,
      sector: filters.value.sector,
      enforcement: filters.value.enforcement,
    }),
});

const requirements = computed(() => requirementsQuery.data.value ?? []);
const isLoading = computed(() => requirementsQuery.isLoading.value);

const hasActiveSearchOrFilters = computed(
  () =>
    !!searchInput.value ||
    filters.value.jurisdiction.length > 0 ||
    filters.value.type.length > 0 ||
    filters.value.sector.length > 0 ||
    filters.value.enforcement.length > 0
);

function clearAll() {
  searchInput.value = '';
  search.value = '';
  filters.value = { jurisdiction: [], type: [], sector: [], enforcement: [] };
}

function toggleFilterValue(key: 'jurisdiction' | 'type' | 'sector' | 'enforcement', value: string) {
  const current = filters.value[key];
  filters.value = {
    ...filters.value,
    [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
  };
}

function isFilterActive(
  key: 'jurisdiction' | 'type' | 'sector' | 'enforcement',
  value: string
) {
  return filters.value[key].includes(value);
}

const toggleMutation = useMutation({
  mutationFn: ({ requirementId, enabled }: { requirementId: string; enabled: boolean }) =>
    toggleComplianceRequirement(integrationId.value, requirementId, enabled),
  onMutate: async ({ requirementId, enabled }) => {
    const queryKey = [
      'complianceRequirementsList',
      integrationId.value,
      search.value,
      filters.value,
    ];
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueryData<TComplianceRequirementListItem[]>(queryKey);
    if (previous) {
      queryClient.setQueryData<TComplianceRequirementListItem[]>(
        queryKey,
        previous.map((r) => (r.id === requirementId ? { ...r, isEnabled: enabled } : r))
      );
    }
    return { previous, queryKey };
  },
  onError: (_err, _vars, context) => {
    if (context?.previous) {
      queryClient.setQueryData(context.queryKey, context.previous);
    }
    toast.error({ message: 'Failed to update compliance scope' });
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['complianceSummary'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanReport'] });
    queryClient.invalidateQueries({ queryKey: ['versionReport'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanPage'] });
    queryClient.invalidateQueries({ queryKey: ['appVersionPage'] });
    queryClient.invalidateQueries({ queryKey: ['folders'] });
  },
});

function onToggleRequirement(req: TComplianceRequirementListItem) {
  toggleMutation.mutate({ requirementId: req.id, enabled: !req.isEnabled });
}

const allEnabled = computed(
  () => requirements.value.length > 0 && requirements.value.every((r) => r.isEnabled)
);

const bulkMutation = useMutation({
  mutationFn: ({ ids, enabled }: { ids: string[]; enabled: boolean }) =>
    bulkToggleComplianceRequirements(integrationId.value, ids, enabled),
  onMutate: async ({ ids, enabled }) => {
    const queryKey = [
      'complianceRequirementsList',
      integrationId.value,
      search.value,
      filters.value,
    ];
    await queryClient.cancelQueries({ queryKey });
    const previous = queryClient.getQueryData<TComplianceRequirementListItem[]>(queryKey);
    if (previous) {
      const idSet = new Set(ids);
      queryClient.setQueryData<TComplianceRequirementListItem[]>(
        queryKey,
        previous.map((r) => (idSet.has(r.id) ? { ...r, isEnabled: enabled } : r))
      );
    }
    return { previous, queryKey };
  },
  onError: (_err, _vars, context) => {
    if (context?.previous) {
      queryClient.setQueryData(context.queryKey, context.previous);
    }
    toast.error({ message: 'Failed to update compliance scope' });
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['complianceSummary'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanReport'] });
    queryClient.invalidateQueries({ queryKey: ['versionReport'] });
    queryClient.invalidateQueries({ queryKey: ['singleScanPage'] });
    queryClient.invalidateQueries({ queryKey: ['appVersionPage'] });
    queryClient.invalidateQueries({ queryKey: ['folders'] });
  },
});

function onBulkToggle() {
  if (requirements.value.length === 0) return;
  const enabled = !allEnabled.value;
  bulkMutation.mutate({ ids: requirements.value.map((r) => r.id), enabled });
}

function toggleDropdown(name: string) {
  openDropdown.value = openDropdown.value === name ? null : name;
}

const filterGroups: Array<{ label: string; key: TFilterKey; options: string[] }> = [
  { label: 'Jurisdiction', key: 'jurisdiction', options: JURISDICTIONS },
  { label: 'Type', key: 'type', options: TYPES },
  { label: 'Domain', key: 'sector', options: SECTORS },
  { label: 'Enforcement', key: 'enforcement', options: ENFORCEMENTS },
];

const TYPE_BADGE_CLASS: Record<string, string> = {
  Standard: 'tw:bg-[#e8f0ff] tw:text-[#3180f6]',
  Catalog: 'tw:bg-[#efe8ff] tw:text-[#7c4dff]',
  Guideline: 'tw:bg-[#e6f7ee] tw:text-[#1ea65a]',
  Framework: 'tw:bg-[#fff0e3] tw:text-[#e8821a]',
  Regulation: 'tw:bg-[#fde7ec] tw:text-[#e84a6e]',
};

function typeBadgeClass(type: string) {
  return TYPE_BADGE_CLASS[type] ?? 'tw:bg-black/5 tw:text-secondary';
}

const referrer = computed(() => {
  const q = unref(instance?.proxy.$route)?.query?.from;
  return typeof q === 'string' ? q : '';
});

function returnToReport() {
  if (referrer.value) {
    instance?.proxy.$router.push(referrer.value);
  } else {
    instance?.proxy.$router.back();
  }
}

const isReturnBarDismissed = ref(false);
const showReturnBar = computed(() => !!referrer.value && !isReturnBarDismissed.value);

return { __sfc: true,TYPES, SECTORS, ENFORCEMENTS, JURISDICTIONS, instance, integrationId, searchInput, search, searchDebounce, filters, openDropdown, filtersRef, onClickOutside, queryClient, toast, requirementsQuery, requirements, isLoading, hasActiveSearchOrFilters, clearAll, toggleFilterValue, isFilterActive, toggleMutation, onToggleRequirement, allEnabled, bulkMutation, onBulkToggle, toggleDropdown, filterGroups, TYPE_BADGE_CLASS, typeBadgeClass, referrer, returnToReport, isReturnBarDismissed, showReturnBar, SearchInput }
}

})